<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import MessageApi from 'dashboard/api/inbox/message';
import types from 'dashboard/store/mutation-types';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({ message: { type: Object, required: true } });
const { t } = useI18n();
const route = useRoute();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const scope = computed(
  () => `${route.params.accountId}:${currentChat.value?.id}`
);
const mediaFailed = ref(false);
const navigationFailed = ref(false);
const opening = ref(false);
const unavailable = computed(
  () =>
    !props.message.id ||
    props.message.contentAttributes?.deleted ||
    props.message.content_attributes?.deleted
);
const loading = computed(() => props.message.replyState === 'loading');
const image = computed(() =>
  props.message.attachments?.find(a => (a.fileType ?? a.file_type) === 'image')
);
const imageUrl = computed(
  () =>
    image.value?.thumbUrl ||
    image.value?.thumb_url ||
    image.value?.dataUrl ||
    image.value?.data_url
);
const preview = computed(() => {
  if (loading.value) return t('CONVERSATION.QUOTED_MESSAGE.LOADING');
  if (unavailable.value) return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
  if (props.message.content) return props.message.content;
  const attachment = props.message.attachments?.[0];
  return attachment
    ? t(
        `CHAT_LIST.ATTACHMENTS.${attachment.fileType ?? attachment.file_type}.CONTENT`
      )
    : t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
watch([() => props.message.id, imageUrl, scope], () => {
  mediaFailed.value = false;
  navigationFailed.value = false;
});

const openOriginal = async () => {
  if (unavailable.value || opening.value) return;
  const requestedScope = scope.value;
  const conversationId = currentChat.value.id;
  const messageId = props.message.id;
  opening.value = true;
  navigationFailed.value = false;
  try {
    // Resolve again through the authorized conversation, not an attachment URL or global ID.
    const loadedIds =
      currentChat.value.messages?.map(message => message.id) || [];
    let before = loadedIds.length
      ? Math.max(messageId + 1, Math.min(...loadedIds))
      : messageId + 1;
    const messages = [];
    // Fill the interval, not just one old message: otherwise scrolling history
    // would skip everything between that original and the loaded timeline.
    while (before > messageId) {
      // The next cursor comes from this response; parallel requests would overlap.
      // eslint-disable-next-line no-await-in-loop
      const { data } = await MessageApi.getPreviousMessages({
        conversationId,
        after: messageId,
        before,
      });
      if (requestedScope !== scope.value) return;
      if (!data.payload.length) break;
      messages.unshift(...data.payload);
      const oldest = Math.min(...data.payload.map(message => message.id));
      if (oldest >= before) break;
      before = oldest;
    }
    if (requestedScope !== scope.value) return;
    const original = messages.find(message => message.id === messageId);
    if (!original || original.content_attributes?.deleted) {
      navigationFailed.value = true;
      return;
    }
    if (
      !currentChat.value.messages?.some(message => message.id === messageId)
    ) {
      store.commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: conversationId,
        data: messages.filter(
          message =>
            !currentChat.value.messages?.some(
              loaded => loaded.id === message.id
            )
        ),
      });
    }
    await nextTick();
    if (requestedScope !== scope.value) return;
    const target = document.getElementById(`message${messageId}`);
    if (!target) {
      navigationFailed.value = true;
      return;
    }
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
  } catch {
    if (requestedScope === scope.value) navigationFailed.value = true;
  } finally {
    opening.value = false;
  }
};
</script>

<template>
  <div class="mb-2 rounded-lg bg-n-alpha-black1" data-testid="quoted-message">
    <button
      type="button"
      class="flex items-start gap-2 w-full min-w-0 p-2 text-start rounded-lg border-s-2 border-n-slate-9 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-default"
      :disabled="unavailable || opening"
      :aria-busy="loading || opening"
      :aria-label="
        unavailable ? preview : t('CONVERSATION.QUOTED_MESSAGE.OPEN')
      "
      @click="openOriginal"
    >
      <img
        v-if="!unavailable && imageUrl && !mediaFailed"
        :src="imageUrl"
        :alt="t('CONVERSATION.QUOTED_MESSAGE.IMAGE')"
        class="w-12 h-12 shrink-0 rounded object-cover"
        @error="mediaFailed = true"
      />
      <span class="min-w-0 flex-1">
        <span
          v-if="!unavailable && message.sender?.name"
          class="block text-xs font-semibold truncate"
        >
          {{ message.sender.name }}
        </span>
        <span class="block text-sm line-clamp-2 break-words">{{
          preview
        }}</span>
        <span
          v-if="!unavailable && image && (!imageUrl || mediaFailed)"
          class="block text-xs text-n-slate-11"
          role="status"
        >
          {{ t('CONVERSATION.QUOTED_MESSAGE.MEDIA_UNAVAILABLE') }}
        </span>
      </span>
    </button>
    <p
      v-if="navigationFailed"
      class="px-2 pb-2 text-xs text-n-ruby-11"
      role="alert"
    >
      {{ t('CONVERSATION.QUOTED_MESSAGE.OPEN_FAILED') }}
    </p>
  </div>
</template>
